import Alert, { AlertProps } from 'components/padi-ui/alert';
import { PromotionalTileProps } from 'components/padi-ui/promotional-tile/types';
import { ErrorDisplay } from 'helpers/errorDisplay';
import { useFormat } from 'helpers/hooks/useFormat';
import { useParams } from 'next/navigation';
import React, { memo, useEffect, useMemo, useState } from 'react';
import { Category } from 'shared/types/product/Category';
import { Product } from 'shared/types/product/Product';
import { ProjectConfig } from 'types/project-config';
import AccumalativeTrace from './components/accumalative-trace';
import CategoryMenu from './components/category-menu';
import CertList from './components/cert-list';
import CourseCategoryHeading from './components/course-category-heading';
import CurrentRefinements from './components/current-refinements';
import DesktopFacets from './components/desktop-facets';
import List from './components/list';
import MobileFacets from './components/mobile-facets';
import CertECardNav from './components/sidebar/cert-ecard-nav';
import CertPhysicalCardNav from './components/sidebar/cert-physical-card-nav';

export interface Props {
  categories: Category[];
  products: Product[];
  projectConfig?: ProjectConfig;
  promotionalTileProps?: PromotionalTileProps;
}

const ProductList: React.FC<Props> = ({ products, categories, projectConfig, promotionalTileProps }) => {
  const { slug } = useParams(); // In the useProductList hook, slug is currently forced to a string. We now need to support it as an array of strings.

  const { formatMessage: formatMessageProduct } = useFormat({ name: 'product' });

  const [alert, setAlert] = useState<AlertProps>();
  useEffect(() => {
    if (ErrorDisplay.alert) {
      setAlert({ ...ErrorDisplay.alert });
      ErrorDisplay.clearAlert();
    }
  }, [alert]);

  const currentCategory = useMemo(() => {
    const url = (Array.isArray(slug) ? slug[slug.length - 1] : slug) || '';
    /**
     * We define a new link certification-card-ecard to filter certification-card.
     * But the data is setting certification-card.
     */
    return url === 'certification-card' ? 'certification-card-ecard' : url;
  }, [slug]);

  let activeCategory = useMemo(
    () => categories.find((category) => category.slug === currentCategory),
    [categories, currentCategory],
  );

  let activeParentCategory = {} as Category;

  const findActiveCategory = (categories: Category[] | undefined) => {
    if (!categories) return;
    categories.map(function (category) {
      if (category?.subCategories) {
        category.subCategories?.some((subCategory) => {
          if (subCategory.slug === currentCategory) {
            activeCategory = subCategory;
            activeParentCategory = category;
          } else {
            findActiveCategory(category.subCategories);
          }
        });
      }
    });
  };

  if (!activeCategory) {
    findActiveCategory(categories);
  }

  function loadSidebar(categoryKey: string | null | undefined) {
    switch (categoryKey) {
      case 'certification-card-physical':
      case 'certification-card-type-physical':
      case 'certification-card-special-card-physical':
        return <CertPhysicalCardNav categories={categories} activeCategory={activeCategory ?? {}} />;
      case 'certification-card':
      case 'certification-card-ecard':
      case 'certification-card-type-ecard':
      case 'certification-card-special-card-ecard':
        return <CertECardNav categories={categories} activeCategory={activeCategory ?? {}} />;
      default:
        return <CategoryMenu categories={categories} activeCategory={activeCategory ?? {}} />;
    }
  }

  const listContent = useMemo(() => {
    const categoryKey = activeParentCategory?.key ?? activeCategory?.key ?? null;
    if (categoryKey?.includes('certification-card')) {
      return {
        list: (
          <CertList products={products} projectConfig={projectConfig} promotionalTileProps={promotionalTileProps} />
        ),
        title: formatMessageProduct({
          id: 'cert.category.note',
          defaultMessage:
            'Not all certification card designs are available for every certification. Select a card to confirm that the design is available for your certification(s).',
        }),
      };
    } else {
      return {
        list: <List products={products} projectConfig={projectConfig} promotionalTileProps={promotionalTileProps} />,
      };
    }
  }, [
    activeParentCategory?.key,
    activeCategory?.key,
    products,
    projectConfig,
    promotionalTileProps,
    formatMessageProduct,
  ]);

  return (
    <div className="min-h-screen bg-white py-40">
      {alert && (
        <div className="flex justify-center">
          <Alert alertType={`${alert.alertType}`} body={`${alert.body}`} />
        </div>
      )}
      <CourseCategoryHeading
        categories={activeParentCategory?.subCategories ?? categories}
        categoryId={activeCategory?.categoryId}
      />
      {listContent.title && (
        <p className="mx-auto mb-16 pt-32 text-center text-lg font-normal text-slate-700" style={{ maxWidth: '580px' }}>
          {listContent.title}
        </p>
      )}
      <div className="mx-auto max-w-7xl px-24 lg:px-32">
        <MobileFacets
          categories={categories}
          activeCategory={activeCategory ?? {}}
          activeParentCategory={activeParentCategory ?? {}}
        />
        <main>
          <DesktopFacets {...{ categories }} />

          <section aria-labelledby="products-heading" className="pb-24 pt-6">
            <h2 id="products-heading" className="sr-only">
              {formatMessageProduct({
                id: 'products',
                defaultMessage: 'Products',
              })}
            </h2>

            <div className="grid grid-cols-1 gap-x-8 gap-y-10 lg:grid-cols-4">
              {/* Filters */}
              <div className="hidden pt-32 lg:block">
                {loadSidebar(activeParentCategory?.key ?? activeCategory?.key ?? null)}
              </div>

              {/* Product grid */}
              <div className="lg:col-span-3">
                <div className="relative">
                  <CurrentRefinements />
                  {listContent.list}
                  <AccumalativeTrace currentItems={products.length} />
                </div>
              </div>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
};

export default memo(ProductList);
